/**
 * AI Model Settings Component - Judge (arbiter) model picker.
 *
 * The Judge model is sent to the backend as `options.model` and is used to break
 * ties when the voter pool disagrees in consensus mode. Voter-list membership is
 * configured separately per-die in ProviderList (Settings → My Dice).
 *
 * Three options:
 * - Fast (gpt-4.1-mini): default, free, quick recognition
 * - Accurate — ChatGPT (gpt-5.2): login-gated, higher accuracy
 * - Accurate — Claude (claude-opus-4-7): login-gated, Anthropic family
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthStore } from '@/stores/authStore';
import { useSoulBondsStore } from '@/stores/soulBondsStore';
import type { AIRecognitionModel } from '@/types/soulBond';

const SERVICE_TAG = '[AI_MODEL_SETTINGS]';

interface AIModelSettingsProps {
  /** When true, renders without Card wrapper for use inside Collapsible */
  embedded?: boolean;
}

/**
 * Describes one selectable Judge model row.
 */
interface ModelOption {
  id: AIRecognitionModel;
  title: string;
  description: string;
  cost: string;
  requiresAuth: boolean;
  isDefault?: boolean;
}

const MODEL_OPTIONS: ModelOption[] = [
  {
    id: 'gpt-4.1-mini',
    title: 'Fast',
    description: 'Uses GPT-4.1 mini for quick recognition',
    cost: 'Cost: ~$0.0004/image',
    requiresAuth: false,
    isDefault: true
  },
  {
    id: 'gpt-5.2',
    title: 'Accurate \u2014 ChatGPT',
    description: 'Uses GPT-5.2 for best accuracy with challenging dice',
    cost: 'Cost: ~$0.01/image',
    requiresAuth: true
  },
  {
    id: 'claude-opus-4-7',
    title: 'Accurate \u2014 Claude',
    description: 'Uses Claude Opus 4.7 for best accuracy with challenging dice',
    cost: 'Cost: ~$0.01/image',
    requiresAuth: true
  }
];

/**
 * Settings card for AI recognition Judge model selection.
 * Provides three radio buttons (Fast / Accurate — ChatGPT / Accurate — Claude).
 * Premium rows are disabled with a Sign-up CTA for unauthenticated users.
 */
export function AIModelSettings({ embedded = false }: AIModelSettingsProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { appSettings, updateSettings } = useSoulBondsStore();
  const currentModel: AIRecognitionModel = appSettings.aiRecognitionModel ?? 'gpt-4.1-mini';

  /**
   * Handle model change and persist to settings.
   */
  const handleModelChange = (model: AIRecognitionModel) => {
    console.info(`${SERVICE_TAG} AI recognition model changed`, {
      previousModel: currentModel,
      newModel: model
    });
    updateSettings({ aiRecognitionModel: model });
  };

  const content = (
    <div className="space-y-3">
      <label className="text-sm font-medium">Recognition Model (Judge)</label>
      <p className="text-xs text-muted-foreground -mt-2">
        Chosen model decides tie-breakers in consensus mode.
      </p>
      <div className="space-y-2">
        {MODEL_OPTIONS.map((option) => {
          const isGated = option.requiresAuth && !isAuthenticated;
          return (
            <label
              key={option.id}
              className={`flex items-start space-x-3 ${
                isGated ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name="aiRecognitionModel"
                value={option.id}
                checked={currentModel === option.id}
                onChange={() => handleModelChange(option.id)}
                disabled={isGated}
                className="h-4 w-4 mt-1 text-primary"
              />
              <div>
                <span className="font-medium">{option.title}</span>
                {option.isDefault && (
                  <span className="text-muted-foreground text-sm ml-2">(Default)</span>
                )}
                <p className="text-xs text-muted-foreground">{option.description}</p>
                <p className="text-xs text-muted-foreground mt-1">{option.cost}</p>
                {isGated && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <button
                      type="button"
                      onClick={() => navigate('/login', { state: { mode: 'register' } })}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign up
                    </button>
                    {' '}for a free account to unlock {option.title}.
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground pt-2 border-t border-border/50">
        Most users should start with Fast mode. Switch to an Accurate mode only if experiencing
        recognition issues with unusual lighting, stylized fonts, or decorative dice.
      </p>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">AI Recognition Model</CardTitle>
            <CardDescription>
              Choose which AI acts as the judge/arbiter for dice recognition
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
